import _ from 'lodash';
import { createAsyncThunk, createSelector, createSlice, PayloadAction, SerializedError, } from "@reduxjs/toolkit";
import { RootState } from "app/realtime-store/redux-store";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";
import { useSelector } from "react-redux";
import Session from "users/session/session";
import BarebonesWorkAgreementInfoAPIResponse from "work/entities/work-agreement/api/request-contracts/barebones-work-agreement-api-response";
import WorkAgreementParameters, { WorkAgreementFilterParameters, WorkAgreementOrderField, WorkAgreementOrderParameters } from "work/entities/work-agreement/api/request-contracts/work-agreement-parameters";
import WorkAgreementAPIService, { WorkAgreementNotFoundError } from "work/entities/work-agreement/api/work-agreement-api-service";
import WorkAgreement from "work/entities/work-agreement/work-agreement";
import { OrderDirection } from 'common/contracts/order-direction';
import PaginatedResponse from 'common/contracts/paginated-response';

type WorkAgreementStoreState = {
  counts: {
    active: number | undefined;
    archived: number | undefined;
  }  
  byId: {
    entries: Record<string, WorkAgreement | null | undefined>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  bareBones: {
    entries: Record<string, BarebonesWorkAgreementInfoAPIResponse | undefined>;
    loading: Record<string, boolean>;
  };
  byQuery: {
    entries: Record<string, PaginatedResponse<WorkAgreement>>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  };
  interfaceQueries: {
    entries: Record<string, WorkAgreementQuery>;
  };
};
const initialState: WorkAgreementStoreState = {
  counts: {
    active: undefined,
    archived: undefined,
  },
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  bareBones: {
    entries: {},
    loading: {}
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

export class WorkAgreementQuery {
  constructor(
    public workAgreementParams: WorkAgreementParameters,
    public pagination?: PaginationParameters
  ) {
  }

  public asSearchParams(): URLSearchParams {
    return new URLSearchParams([
      ...this.workAgreementParams.asSearchParams(),
      ...(this.pagination?.asSearchParams() ?? []),
    ]);
  }
}

export const loadActiveWorkAgreementCount = createAsyncThunk(
  "proposals/getActiveWorkAgreementCount",
  async ({ session }: { session: Session }, thunkAPI) => {
    const apiService = new WorkAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      const query = new WorkAgreementQuery(
        new WorkAgreementParameters(
          new WorkAgreementFilterParameters(),
          new WorkAgreementOrderParameters(WorkAgreementOrderField.Start, OrderDirection.Descending)
        ),
        new PaginationParameters(0, 0)
      );
      const response = await apiService.getWorkAgreementsAsync(query, abortController);
      return response.headers.totalElements;
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  });

export const loadArchivedWorkAgreementCount = createAsyncThunk(
  "proposals/getArchivedWorkAgreementCount",
  async ({ session }: { session: Session }, thunkAPI) => {
    const apiService = new WorkAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      const query = new WorkAgreementQuery(
        new WorkAgreementParameters(
          new WorkAgreementFilterParameters({
            isArchived: true
          }),
          new WorkAgreementOrderParameters(WorkAgreementOrderField.Start, OrderDirection.Descending)
        ),
        new PaginationParameters(0, 0)
      );
      const response = await apiService.getWorkAgreementsAsync(query, abortController);
      return response.headers.totalElements;
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  });

export const populateWorkAgreement = createAsyncThunk(
  "work/getWorkAgreementById",
  async ({ session, id }: { session: Session; id: Guid; }, thunkAPI) => {
    const apiService = new WorkAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getWorkAgreementByIdAsync(id, abortController);
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const populateWorkAgreementsByQuery = createAsyncThunk(
  "work/getWorkAgreementsByQuery",
  async (
    { session, query }: { session: Session; query: WorkAgreementQuery },
    thunkAPI
  ) => {
    if (!query) return;

    const apiService = new WorkAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getWorkAgreementsAsync(query, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const populateBarebonesWorkAgreements = createAsyncThunk<
  BarebonesWorkAgreementInfoAPIResponse[],
  { session: Session; workAgreementIds: Guid[] }
>(
  "work/populateBarebonesWorkAgreements",
  async ({ session, workAgreementIds }: { session: Session; workAgreementIds: Guid[] }, thunkAPI) => {
    if (!workAgreementIds.length) return [];

    const store: { workAgreements: WorkAgreementStoreState } = thunkAPI.getState() as { workAgreements: WorkAgreementStoreState };
    const missingWorkAgreementsIds = _.uniqWith(
      workAgreementIds.filter((workAgreementId) => !store.workAgreements.bareBones.entries[workAgreementId.value]),
      (a, b) => a.isEqualTo(b)
    );
    if (!missingWorkAgreementsIds.length) return [];

    const apiService = new WorkAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getWorkAgreementsByIds(workAgreementIds, abortController);
    } catch (error) {
      console.error(error);
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

const workAgreementsSlice = createSlice({
  name: "workAgreements",
  initialState,
  reducers: {
    addWorkAgreement: (state, action: PayloadAction<WorkAgreement>) => {
      const workAgreement = action.payload;
      if (!workAgreement.id) {
        console.error("Work agreement does not have an id");
        return;
      }

      state.byId.entries[workAgreement.id.value] = workAgreement;
      state.counts = {
        active: undefined,
        archived: undefined,
      }      
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };

    },
    removeWorkAgreement: (state, action: PayloadAction<Guid>) => {
      state.byId.entries[action.payload.value] = null;
      state.counts = {
        active: undefined,
        archived: undefined,
      }      
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    updateWorkAgreementInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: WorkAgreementQuery;
      }>
    ) => {
      state.interfaceQueries.entries[action.payload.interfaceName] =
        action.payload.query;
    },
    updateAllQueriesLoading: (state, action: PayloadAction<boolean>) => {
      for (const [query] of Object.entries(state.byQuery.loading)) {
        state.byQuery.loading[query] = action.payload;
      }
    }
  },
  extraReducers: (builder) => {
    builder.addCase(
      loadActiveWorkAgreementCount.fulfilled,
      (state, action) => {
        if (action.payload === undefined || action.payload === null) {
          return;
        }
        state.counts.active = action.payload;
      }
    );
    builder.addCase(
      loadArchivedWorkAgreementCount.fulfilled,
      (state, action) => {
        if (action.payload === undefined || action.payload === null) {
          return;
        }
        state.counts.archived = action.payload;
      }
    );    
    builder.addCase(
      populateWorkAgreement.pending,
      (state, action) => {
        const workAgreementId = action.meta.arg.id.value;
        state.byId.loading[workAgreementId] = true;
        state.byId.error[workAgreementId] = null;
      }
    );
    builder.addCase(
      populateWorkAgreement.fulfilled,
      (state, action) => {
        const workAgreement = action.payload;
        if (!workAgreement.id) {
          console.error(
            "Work agreement does not have an id",
            workAgreement
          );
          return;
        }
        state.byId.entries[workAgreement.id.value] = workAgreement;
        state.byQuery = {
          entries: {},
          loading: {},
          error: {},
        };
        state.byId.loading[workAgreement.id.value] = false;
        state.byId.error[workAgreement.id.value] = null;
      }
    );
    builder.addCase(
      populateWorkAgreement.rejected,
      (state, action) => {
        const workAgreementId = action.meta.arg.id.value;
        state.byId.loading[workAgreementId] = false;
        state.byId.error[workAgreementId] = action.error;
        if (action.payload instanceof WorkAgreementNotFoundError) {
          state.byId.entries[workAgreementId] = null;
        }
      }
    );

    builder.addCase(
      populateWorkAgreementsByQuery.pending,
      (state, action) => {
        if (!action.meta.arg.query) {
          return;
        }
        const query = action.meta.arg.query
          .asSearchParams()
          .toString();
        state.byQuery.loading[query] = true;
        state.byQuery.error[query] = null;
      }
    );
    builder.addCase(
      populateWorkAgreementsByQuery.fulfilled,
      (state, action) => {
        if (!action.meta.arg.query) {
          return;
        }
        const query = action.meta.arg.query
          .asSearchParams()
          .toString();
        if (!query || !action.payload) {
          return;
        }
        state.byQuery.loading[query] = false;
        state.byQuery.entries[query] = action.payload;
      }
    );
    builder.addCase(
      populateWorkAgreementsByQuery.rejected,
      (state, action) => {
        if (!action.meta.arg.query) {
          return;
        }
        const query = action.meta.arg.query
          .asSearchParams()
          .toString();
        state.byQuery.loading[query] = false;
        state.byQuery.error[query] = action.error;
      }
    );

    builder.addCase(
      populateBarebonesWorkAgreements.pending,
      (state, action) => {
        if (!action.meta.arg.workAgreementIds?.length) return;
        for (const workAgreementId of action.meta.arg.workAgreementIds) {
          state.bareBones.loading[workAgreementId.value] = true;
        }
      }
    );
    builder.addCase(
      populateBarebonesWorkAgreements.fulfilled,
      (state, action) => {
        for (const workAgreement of action.payload) {
          if (!workAgreement.id) {
            console.error("Work agreement does not have an id", workAgreement);
            continue;
          }
          state.bareBones.entries[workAgreement.id] = workAgreement;
          state.bareBones.loading[workAgreement.id] = false;
        }
      }
    );
    builder.addCase(
      populateBarebonesWorkAgreements.rejected,
      (state, action) => {
        for (const workAgreementId of action.meta.arg.workAgreementIds) {
          state.bareBones.loading[workAgreementId.value] = false;
        }
      }
    );
  },
});

export const {
  addWorkAgreement,
  removeWorkAgreement,
  updateWorkAgreementInterfaceQuery,
  updateAllQueriesLoading
} = workAgreementsSlice.actions;

export const getWorkAgreementById = (id?: Guid) =>
  useSelector((state: RootState) => {
    if (!id) return undefined;
    return state.workAgreements?.byId.entries[id.value];
  });
export const getIsLoadingWorkAgreementById = (id?: Guid) =>
  useSelector((state: RootState) => {
    if (!id) return false;
    return (
      state.workAgreements?.byId.loading[id.value] || state.workAgreements?.bareBones.loading[id.value]
    );
  });
export const getErrorLoadingWorkAgreementById = (id?: Guid) =>
  useSelector((state: RootState) => {
    if (!id) return undefined;
    return state.workAgreements?.byId.error[id.value] || state.workAgreements?.bareBones.entries[id.value]?.error;
  });
export const getWorkAgreementQueryByInterface = (interfaceName: string) =>
  useSelector(
    (state: RootState) =>
      state.workAgreements.interfaceQueries.entries[interfaceName]
  );
export const getWorkAgreementsByQuery = (query: WorkAgreementQuery) =>
  useSelector((state: RootState) => {
    if (!query) {
      return undefined;
    }
    const searchParams = query.asSearchParams().toString();
    return state.workAgreements?.byQuery.entries[searchParams];
  });
export const getIsLoadingWorkAgreementsByQuery = (query: WorkAgreementQuery) =>
  useSelector((state: RootState) => {
    if (!query) {
      return false;
    }
    const searchParams = query.asSearchParams().toString();
    return state.workAgreements?.byQuery.loading[searchParams];
  });
export const getErrorLoadingWorkAgreementsByQuery = (query: WorkAgreementQuery) =>
  useSelector((state: RootState) => {
    if (!query) {
      return false;
    }
    const searchParams = query.asSearchParams().toString();
    return state.workAgreements?.byQuery.error[searchParams];
  });
export const getWorkAgreementsByIds: (ids: Guid[]) => BarebonesWorkAgreementInfoAPIResponse[] = (ids: Guid[]) =>
  useSelector(
    createSelector(
      (state: RootState) => state.workAgreements.bareBones.entries,
      (entries) => ids
        .map(id => entries[id.toString()])
        .filter(workAgreement => workAgreement !== undefined)
    )
  );
export const getLoadingWorkAgreementIds = () => {
  const state = useSelector((state: RootState) => state.workAgreements);
  const detailedIds = Object.keys(state.byId).filter((workId) => state.byId.loading[workId]).map((workId) => Guid.fromJSON(workId));
  const bareBonesIds = Object.keys(state.bareBones.loading).filter((workId) => state.bareBones.loading[workId]).map((workId) => Guid.fromJSON(workId));
  return [...detailedIds, ...bareBonesIds];
}
export type WorkAgreementError = {
  id: Guid;
  error: SerializedError;
}
export const getActiveWorkAgreementCount = () =>
  useSelector((state: RootState) => state.workAgreements.counts.active);
export const getArchivedWorkAgreementCount = () =>
  useSelector((state: RootState) => state.workAgreements.counts.archived);

export const getAreErrorsLoadingWorkAgreementsByIds = (ids: Guid[]) =>
  useSelector(
    (state: RootState) => ids.some(id => state.workAgreements.byId.error[id.toString()] || state.workAgreements.bareBones.entries[id.toString()]?.error)
  );
export const getAllWorkAgreementErrors = (): WorkAgreementError[] => {
  const state = useSelector((state: RootState) => state.workAgreements);
  const detailedErrors = Object.keys(state)
    .filter((workId) => workId && state.byId.error[workId])
    .map((workId) => {
      const error = state.byId.error[workId]?.message;
      if (!error) return null;
      const id = Guid.fromJSON(workId);
      if (!id) return null;
      return { id, error };
    }).filter((record) => record?.id) as WorkAgreementError[];
  const bareBonesErrors = Object.keys(state.bareBones.entries).map((workId) => {
    const error = state.bareBones.entries[workId]?.error;
    if (!error) return null;
    const id = Guid.fromJSON(workId);
    return { id, error };
  }).filter((record) => record?.id) as WorkAgreementError[];
  return [...detailedErrors, ...bareBonesErrors]
};

export default workAgreementsSlice;
